import { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
} from "@mui/material";

import SettingsIcon from "@mui/icons-material/Settings";
import PsychologyIcon from "@mui/icons-material/Psychology";
import StorageIcon from "@mui/icons-material/Storage";
import GroupsIcon from "@mui/icons-material/Groups";
import HubIcon from "@mui/icons-material/Hub";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const services = [
  {
    title: "Sales System Architecture",
    dec:"Build a predictable, scalable sales engine from the ground up.",
    points: [
      "Define clear sales processes across every stage",
      "Structure roles, responsibilities, and workflows",
      "Integrate CRM with real-time tracking systems",
      "Eliminate inefficiencies and revenue leakages"
    ],

    icon: <SettingsIcon />,
  },
  {
    title: "Revenue Acceleration",
    dec:"Turn existing demand into faster, higher-value conversions.  ",
    points: [
      "Improve conversion rates across the funnel",
      "Reduce inventory holding cycles",
      "Strengthen closing frameworks and buyer handling",
      "Maximize ROI from current lead flow",
    ],

    icon: <PsychologyIcon />,
  },
  {
    title: "Sales Execution & Deal Control",
    dec:"Bring discipline, visibility, and control into every deal.",
    points: [
      "Standardize deal tracking and follow-up systems",
      "Strengthen negotiation and closing strategies",
      "Monitor team performance with clear metrics",
      "Reduce deal drop-offs and delays"
    ],

    icon: <StorageIcon />,
  },
  {
    title: "Go-To-Market & Positioning",
    dec:"Position your project to attract the right buyers, faster.",
    points: [
      "Define sharp, differentiated market positioning",
      "Align messaging with buyer intent",
      "Optimize marketing channels for quality leads",
      "Strengthen brand perception in competitive markets"
    ],

    icon: <GroupsIcon />,
  },
  {
    title: "Site Visit & Buyer Experience",
    dec:"Convert site visits into decisive buying moments.",
    points: [
      "Design structured site visit journeys",
      "Train teams on buyer psychology and engagement",
      "Improve on-ground experience and presentation",
      "Increase visit-to-booking conversion rates"
    ],

    icon: <HubIcon />,
  },
  {
    title: "Leadership & Strategic Advisory",
    dec:"Drive decisions with clarity, experience, and direction.",
    points: [
      "Provide strategic oversight on sales operations",
      "Enable leadership with data-backed insights",
      "Align teams with business growth objectives",
      "Support high-stakes decision-making"
    ],

    icon: <HubIcon />,
  },
];

const ServicesTabs = () => {
  const [active, setActive] = useState(0);

  return (
    <Box
      sx={{
        background: "#ffffff",
        py: { xs: 10, md: 8 },
      }}
    >
      <Container maxWidth="lg">

        {/* HEADER */}
        <Box sx={{ textAlign: "center", mb: 10 }}>
          <Typography
            variant="h4"
            sx={{

              fontSize: { xs: 30, md: 42 },
              color: "#0b1f33",
              mb: 2,
              fontWeight: 600,
            }}
          >
            Core Engagement Areas
          </Typography>

          <Typography variant="body2" sx={{ color: "#5b6b7c" }}>
            End to end sales systems designed to drive conversions, accelerate revenue, and create predictable outcomes
          </Typography>
        </Box>

        <Grid container spacing={6}>

          {/* LEFT MENU */}
          <Grid item xs={12} md={4}>
            {services.map((item, index) => (
              <Box
                key={index}
                onClick={() => setActive(index)}
                sx={{
                  position: "relative",
                  p: 2.0,
                  mb: 3,
                  cursor: "pointer",
                  borderRadius: "18px",
                  overflow: "hidden",
                  transition: "all 0.35s ease",

                  // 💎 BASE STYLE
                  background:
                    active === index
                      ? "linear-gradient(145deg, rgba(15,99,165,0.12), rgba(15,99,165,0.05))"
                      : "rgba(255,255,255,0.6)",

                  backdropFilter: "blur(10px)",

                  border:
                    active === index
                      ? "1px solid rgba(15,99,165,0.25)"
                      : "1px solid rgba(15,99,165,0.08)",

                  boxShadow:
                    active === index
                      ? `
              0 10px 30px rgba(15,99,165,0.15),
              0 5px 15px rgba(0,0,0,0.05)
            `
                      : "0 4px 12px rgba(0,0,0,0.04)",

                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `
            0 15px 40px rgba(15,99,165,0.18),
            0 8px 20px rgba(0,0,0,0.06)
          `,
                  },

                  // 🔥 GLOSS LIGHT
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: "-50%",
                    left: "-30%",
                    width: "150%",
                    height: "150%",
                    background: `
            radial-gradient(
              circle,
              rgba(255,255,255,0.5),
              transparent 60%
            )
          `,
                    opacity: active === index ? 0.6 : 0.2,
                  },
                }}
              >
                {/* CONTENT */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    position: "relative",
                    zIndex: 2,
                    textAlign: 'start'
                  }}
                >
                  {/* ICON */}
                  <Box
                    sx={{
                      color: active === index ? "#0f63a5" : "#6b7c8f",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </Box>

                  {/* TITLE */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: active === index ? "#0f63a5" : "#0b1f33",
                      fontWeight: 700,

                      lineHeight: 1.4,
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>

                {/* 🔥 ACTIVE SIDE GLOW LINE */}
                {active === index && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "4px",
                      background: "linear-gradient(#0f63a5, #6db3ff)",
                      boxShadow: "0 0 10px rgba(15,99,165,0.6)",
                    }}
                  />
                )}
              </Box>
            ))}
          </Grid>

          {/* RIGHT PREMIUM CARD */}
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                p: 6,
                borderRadius: "28px",
                position: "relative",
                overflow: "hidden",
                textAlign: 'start',

                // 💎 LUXURY GRADIENT CARD
                background: `
                  linear-gradient(
                    145deg,
                    #0f63a5 0%,
                    #0c4f85 50%,
                    #083a63 100%
                  )
                `,

                // 💎 DEPTH SHADOW
                boxShadow: `
                  0 25px 80px rgba(15,99,165,0.35),
                  0 10px 30px rgba(0,0,0,0.2)
                `,
              }}
            >
              {/* 🔥 GLOSS LIGHT */}
              <Box
                sx={{
                  position: "absolute",
                  top: "-40%",
                  left: "-20%",
                  width: "150%",
                  height: "150%",
                  background:
                    "radial-gradient(circle, rgba(255,255,255,0.25), transparent 60%)",
                  transform: "rotate(25deg)",
                }}
              />

              {/* CONTENT */}
              <Box sx={{ position: "relative", zIndex: 2 }}>

                {/* ICON */}
                <Box
                  sx={{
                    color: "#ffffff",
                    mb: 2,
                    filter: "drop-shadow(0 0 8px rgba(255,255,255,0.5))",
                  }}
                >
                  {services[active].icon}
                </Box>

                {/* TITLE */}
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: 26,
                    color: "#ffffff",
                    mb: 3,
                    fontWeight: 600,
                  }}
                >
                  {services[active].title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: {xs:17,md:26},
                    color: "#ffffff",
                    mb: 3,
                    fontWeight: 600,
                  }}
                >
                  {services[active].dec}
                </Typography>


                {/* POINTS */}
                {services[active].points.map((point, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start", // better for multi-line text
                      gap: 1.2,
                      mb: 1.5,
                    }}
                  >
                    {/* ✅ ICON */}
                    <CheckCircleIcon
                      sx={{
                        fontSize: 18,
                        color: "#fff", // or white depending on bg
                        mt: "2px",
                      }}
                    />

                    {/* ✅ TEXT */}
                    <Typography
                    variant="body2"
                      sx={{
                        fontSize: 15,
                        color: "rgba(255,255,255,0.9)",
                        lineHeight: 1.6,
                      }}
                    >
                      {point}
                    </Typography>
                  </Box>
                ))}

                {/* OUTCOME */}
                {/* <Typography
                  sx={{
                    fontSize: 15,
                    color: "#ffffff",
                    mt: 4,
                  }}
                >
                  <span style={{ color: "#cfe8ff" }}>Outcome:</span>{" "}
                  {services[active].outcome}
                </Typography> */}
              </Box>

              {/* EDGE HIGHLIGHT */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "28px",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}
              />
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default ServicesTabs;